import React from 'react';
import { Info } from 'lucide-react';
import { Input } from '../../../../../components/input';
import { RequiredLabel } from '../../../../../components/form';
import { FormErrorText } from '../../../../../components/form';
import { AppTooltip } from '../../../../../components/tooltip';
import { MerchantCouponUIState } from '../../data/merchantCouponEditor.data';
import type { MerchantCouponValidationErrors } from '../../schemas/merchantCouponEditor.schema';

interface UsageLimitsSectionProps {
    coupon: MerchantCouponUIState;
    onFieldChange: (field: keyof MerchantCouponUIState, value: any) => void;
    isCreating: boolean;
    errors: MerchantCouponValidationErrors;
}

/* Spec from Figma HTML export – same card/input tokens as Coupon Details */
const labelClass =
    "text-[#75757D] dark:text-gray-400 text-[14px] font-normal font-['Nunito'] leading-[21px]";
const labelOptionalClass =
    "text-[rgba(117,117,125,0.82)] dark:text-gray-400 text-[14px] font-normal font-['Nunito'] leading-[21px]";
const helperClass =
    "text-[#6E7670] dark:text-gray-400 text-[12px] font-normal font-['Segoe_UI'] leading-[18px]";
const inputOutline =
    "rounded-[8px] border border-[#E2E8F0] dark:border-gray-600";
const inputText =
    "text-[14px] font-normal font-['Segoe_UI'] bg-white dark:bg-gray-900 text-[#0A0A0A] dark:text-gray-100 placeholder:text-[#0A0A0A]/50 dark:placeholder:text-gray-400 disabled:opacity-50";

const normalizeUsageLimit = (value: string): number => {
    const normalizedValue = value.replace(/^0+(?=\d)/, '');
    return normalizedValue ? Number(normalizedValue) : 0;
};

export const UsageLimitsSection: React.FC<UsageLimitsSectionProps> = ({
    coupon,
    onFieldChange,
    isCreating,
    errors,
}) => {
    return (
        <div className="self-stretch p-[23.8px] bg-white dark:bg-gray-800 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] dark:outline-gray-600 flex flex-col gap-6">
            {/* Header – pb 8px, border 0.8px #00C49A, title 18px #004232 line-height 27px */}
            <div className="self-stretch pb-2 border-b-[0.80px] border-[#00C49A] dark:border-emerald-500 flex flex-col">
                <h2 className="text-[#004232] dark:text-emerald-100 text-[18px] font-normal font-['Nunito'] leading-[27px]">
                    Usage Limits
                </h2>
            </div>

            {/* Two columns, gap 16px */}
            <div className="self-stretch flex flex-col md:flex-row gap-4">
                {/* Limit Per User – label, input h 42.6, helper */}
                <div className="flex-1 flex flex-col gap-2">
                    <RequiredLabel htmlFor="coupon-limit-per-user" className={labelClass} required>
                        Limit Per User
                    </RequiredLabel>
                    <Input
                        id="coupon-limit-per-user"
                        type="number"
                        min={1}
                        step={1}
                        value={coupon.limit_per_user ?? ''}
                        onChange={(e) => {
                            const normalizedValue = normalizeUsageLimit(e.target.value);
                            e.target.value = String(normalizedValue);
                            onFieldChange('limit_per_user', normalizedValue);
                        }}
                        disabled={isCreating}
                        placeholder="1-3"
                        className={`${inputOutline} ${inputText} h-[42.6px] px-[16.8px] py-[10.6px]`}
                    />
                    <FormErrorText message={errors.limit_per_user} />
                    <p className={helperClass}>
                        Maximum times a single user can redeem this coupon
                    </p>
                </div>

                {/* Limit Per Day (Optional) */}
                <div className="flex-1 flex flex-col gap-2">
                    <div className="flex items-center gap-1 self-stretch">
                        <label htmlFor="coupon-limit-per-day" className={labelOptionalClass}>
                            Limit Per Day (Optional)
                        </label>
                        <AppTooltip
                            title="This is the maximum total number of times this coupon can be exchanged by all users in one day. It is not a per-user limit."
                        >
                            <button
                                type="button"
                                aria-label="Limit per day information"
                                className="inline-flex items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[#00A982] focus:outline-none focus:ring-2 focus:ring-[#00C49A]/40 dark:text-gray-500 dark:hover:text-emerald-400"
                            >
                                <Info className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </AppTooltip>
                    </div>
                    <Input
                        id="coupon-limit-per-day"
                        type="number"
                        min={1}
                        step={1}
                        value={coupon.limit_per_day ?? ''}
                        onChange={(e) =>
                            onFieldChange(
                                'limit_per_day',
                                e.target.value ? Number(e.target.value) : null,
                            )
                        }
                        disabled={isCreating}
                        placeholder="1-20 or leave empty for no daily limit"
                        className={`${inputOutline} ${inputText} h-[42.6px] px-[16.8px] py-[10.6px]`}
                    />
                    <FormErrorText message={errors.limit_per_day} />
                    <p className={helperClass}>
                        Maximum total redemptions allowed per day
                    </p>
                </div>
            </div>
        </div>
    );
};
